'use client';

import { useEffect, useLayoutEffect } from 'react';
import { loadFeatureFlags } from '@/lib/featureFlags';
import {
  afterHydration,
  applyStageMarker,
  clearStageMarker,
  type FaviconRenderer,
  renderStageFavicon,
} from './applyStageMarker';
import { readStageHint, writeStageHint } from './stage';
import { useStageCue } from './useStageCue';

/**
 * Root-mounted (app/layout.tsx) so every route is covered, /auth/* included —
 * the login page is where you pick which account you are using.
 *
 * It also makes the features snapshot load on EVERY route: before it,
 * loadFeatureFlags() had exactly two callers, the (dashboard) and (admin)
 * layouts. load() is idempotent, so pages inside those groups still make one
 * request.
 */
export function StageMarker({
  renderFavicon = renderStageFavicon,
  afterHydration: hydrated = afterHydration,
}: {
  renderFavicon?: FaviconRenderer;
  /** When the hint path may touch the icon links. A seam for tests. */
  afterHydration?: () => Promise<void>;
}) {
  const { cue, authoritative } = useStageCue();

  // React's hydration commit rewrites the hoisted <title>, erasing the prefix
  // STAGE_HINT_SCRIPT painted (the <html> attribute, and so the stripe,
  // survives). A layout effect runs in that commit before it paints, so
  // re-applying the hint here keeps the reset off screen. It also starts the
  // head observer, which re-prefixes any later rewrite before the next paint.
  // The favicon waits for hydration to settle (see afterHydration): pointing
  // the React-owned icon links elsewhere mid-hydration makes React add four
  // more.
  useLayoutEffect(() => {
    const hint = readStageHint();
    if (hint) applyStageMarker(hint, renderFavicon, hydrated());
  }, [renderFavicon, hydrated]);

  useEffect(() => {
    loadFeatureFlags();
  }, []);

  useEffect(() => {
    // Only the server's answer may rewrite the hint or clear the cues. Before it
    // arrives, cue is null on the FIRST render even when a hint exists (the hint
    // is read in an effect), and clearing then would wipe the stripe and title
    // prefix the pre-hydration script just painted — the exact flash the hint
    // exists to prevent. A load error must not erase the hint either.
    // The server's answer arrives after hydration, so it paints the favicon at
    // once; a hint-sourced cue (before the answer, or after a load error)
    // waits, like the layout effect's.
    if (authoritative) writeStageHint(cue);
    if (cue) applyStageMarker(cue, renderFavicon, authoritative ? undefined : hydrated());
    else if (authoritative) clearStageMarker();
  }, [cue, authoritative, renderFavicon, hydrated]);

  return null;
}
